import { Language as LanguageIcon } from '@mui/icons-material'
import {
  alpha,
  ButtonBase,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  type SxProps,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { T } from '@/helpers/T'
import { useLocaleContext } from '@/providers/Global'
import { LOCALES_LIST, type TLocales } from '@/services/Locale'

// NOTE: This is the app-level UI locale selector. Do NOT reuse LocaleToggle
// from `src/components/LocaleToggle/` — that component speaks 'en'|'fr'|'nl'
// and is only used for email-template content language selection.

//	///////////////////////
//	MARK: CONFIG
interface ILocaleEntry {
  code: string
  native: string
}

const LOCALE_LABELS: Record<TLocales, ILocaleEntry> = {
  'en-GB': { code: 'EN', native: 'English' },
  'fr-FR': { code: 'FR', native: 'Français' },
  'nl-NL': { code: 'NL', native: 'Nederlands' },
}

//	///////////////////////
//	MARK: TYPES
export interface ILocaleSelectorProps {
  variant?: 'compact' | 'full' | 'segmented'
  sx?: SxProps<Theme>
  color?: 'inherit' | 'default' | 'primary'
  onDark?: boolean
}

//	///////////////////////
//	MARK: COMPONENT
const LocaleSelectorInternal: React.FC<ILocaleSelectorProps> = ({
  variant = 'compact',
  sx,
  color = 'default',
  onDark = false,
}) => {
  const Locale = useLocaleContext()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSelect = useCallback(
    (next: TLocales) => {
      Locale.change(next)
      setAnchorEl(null)
    },
    [Locale],
  )

  const activeLabel = useMemo(() => LOCALE_LABELS[Locale.active], [Locale.active])

  if (variant === 'segmented') {
    return (
      <Stack
        direction='row'
        spacing={0}
        role='group'
        aria-label='select-language'
        sx={{
          p: 0.5,
          borderRadius: 999,
          bgcolor: (theme) => {
            if (onDark) return alpha(theme.palette.common.white, 0.14)
            return theme.palette.mode === 'light'
              ? alpha(theme.palette.common.black, 0.04)
              : alpha(theme.palette.common.white, 0.08)
          },
          border: (theme) =>
            onDark
              ? `1px solid ${alpha(theme.palette.common.white, 0.28)}`
              : `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          backdropFilter: 'blur(8px)',
          ...sx,
        }}
      >
        {LOCALES_LIST.map((code) => {
          const entry = LOCALE_LABELS[code]
          const isActive = code === Locale.active
          return (
            <Tooltip key={code} title={entry.native} disableInteractive>
              <ButtonBase
                onClick={() => handleSelect(code)}
                aria-pressed={isActive}
                aria-label={entry.native}
                sx={(theme) => {
                  const activeColor = onDark
                    ? theme.palette.primary.dark
                    : theme.palette.primary.contrastText
                  const activeBg = onDark ? theme.palette.common.white : theme.palette.primary.main
                  const inactiveColor = onDark
                    ? alpha(theme.palette.common.white, 0.78)
                    : theme.palette.text.secondary
                  const hoverInactiveBg = onDark
                    ? alpha(theme.palette.common.white, 0.14)
                    : alpha(theme.palette.text.primary, 0.06)
                  const hoverInactiveColor = onDark
                    ? theme.palette.common.white
                    : theme.palette.text.primary
                  const hoverActiveBg = onDark
                    ? theme.palette.common.white
                    : theme.palette.primary.dark
                  return {
                    minWidth: 38,
                    height: 28,
                    px: 1.25,
                    borderRadius: 999,
                    fontFamily: theme.typography.button.fontFamily,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: isActive ? activeColor : inactiveColor,
                    bgcolor: isActive ? activeBg : 'transparent',
                    boxShadow: isActive ? theme.shadows[2] : 'none',
                    transition: theme.transitions.create(
                      ['background-color', 'color', 'box-shadow'],
                      { duration: 180 },
                    ),
                    '&:hover': {
                      bgcolor: isActive ? hoverActiveBg : hoverInactiveBg,
                      color: isActive ? activeColor : hoverInactiveColor,
                    },
                  }
                }}
              >
                {entry.code}
              </ButtonBase>
            </Tooltip>
          )
        })}
      </Stack>
    )
  }

  return (
    <>
      <Tooltip title={<T id='lang.select_language' />}>
        <IconButton
          onClick={handleOpen}
          color={color}
          size={variant === 'compact' ? 'small' : 'medium'}
          sx={sx}
          aria-label='select-language'
        >
          <LanguageIcon fontSize={variant === 'compact' ? 'small' : 'medium'} />
          <Typography
            variant='button'
            sx={{
              ml: 0.75,
              fontWeight: 600,
              fontSize: variant === 'compact' ? '0.75rem' : '0.875rem',
              lineHeight: 1,
            }}
          >
            {variant === 'full' ? `${activeLabel.code} · ${activeLabel.native}` : activeLabel.code}
          </Typography>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {LOCALES_LIST.map((code) => {
          const entry = LOCALE_LABELS[code]
          return (
            <MenuItem
              key={code}
              selected={code === Locale.active}
              onClick={() => handleSelect(code)}
              sx={{ minWidth: 160 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Typography variant='button' sx={{ fontWeight: 600 }}>
                  {entry.code}
                </Typography>
              </ListItemIcon>
              <ListItemText primary={entry.native} />
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export const LocaleSelector = React.memo(LocaleSelectorInternal) as typeof LocaleSelectorInternal
